import {
	PaymentElement,
	useStripe,
	useElements,
} from '@stripe/react-stripe-js'
import { type StripePaymentElementOptions } from '@stripe/stripe-js'
import { useState } from 'react'
import { Button } from './ui/button.tsx'
import { Icon } from './ui/icon.tsx'

type StripePaymentFormProps = {
	clientSecret: string
	amount: number
	planName: string
	returnUrl: string
}

export function StripePaymentForm({
	amount,
	planName,
	returnUrl,
}: StripePaymentFormProps) {
	const stripe = useStripe()
	const elements = useElements()
	const [isProcessing, setIsProcessing] = useState(false)
	const [errorMessage, setErrorMessage] = useState<string | null>(null)

	const paymentElementOptions: StripePaymentElementOptions = {
		layout: 'tabs',
	}

	const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault()

		if (!stripe || !elements) {
			// Stripe.js hasn't loaded yet
			return
		}

		setIsProcessing(true)
		setErrorMessage(null)

		const { error } = await stripe.confirmPayment({
			elements,
			confirmParams: {
				return_url: returnUrl,
			},
		})

		// This point will only be reached if there's an immediate error
		// Otherwise, the customer will be redirected to the `return_url`
		if (error) {
			if (error.type === 'card_error' || error.type === 'validation_error') {
				setErrorMessage(error.message || 'An error occurred')
			} else {
				setErrorMessage('An unexpected error occurred.')
			}
		}

		setIsProcessing(false)
	}

	return (
		<form onSubmit={handleSubmit} className="space-y-6">
			<div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
				<div className="flex items-center justify-between">
					<div>
						<p className="text-sm font-medium text-gray-600">Plan</p>
						<p className="text-lg font-semibold text-gray-900">{planName}</p>
					</div>
					<div className="text-right">
						<p className="text-sm font-medium text-gray-600">Total</p>
						<p className="text-2xl font-bold text-purple-600">
							£{(amount / 100).toFixed(2)}
						</p>
						<p className="text-xs text-gray-500">per year</p>
					</div>
				</div>
			</div>

			<PaymentElement options={paymentElementOptions} />

			{errorMessage && (
				<div className="rounded-lg border border-red-200 bg-red-50 p-4">
					<div className="flex items-start">
						<Icon name="cross-1" className="mr-2 h-5 w-5 text-red-500" />
						<p className="text-sm text-red-700">{errorMessage}</p>
					</div>
				</div>
			)}

			<Button
				type="submit"
				disabled={isProcessing || !stripe || !elements}
				className="w-full bg-purple-600 text-white hover:bg-purple-700 disabled:bg-gray-300"
				size="lg"
			>
				{isProcessing ? (
					<>
						<Icon name="update" className="mr-2 h-5 w-5 animate-spin" />
						Processing...
					</>
				) : (
					<>
						<Icon name="lock-closed" className="mr-2 h-5 w-5" />
						Pay £{(amount / 100).toFixed(2)}
					</>
				)}
			</Button>

			<p className="text-center text-xs text-gray-500">
				Your payment is secured by Stripe. We never store your card details.
			</p>
		</form>
	)
}
